// frontend/src/marketing/pages/BlogPostPage.tsx
//
// Single blog post at /blog/:slug. Fetches from /api/public/site/blog/:slug
// and renders the post body via our tiny markdown.ts helper.

import * as React from 'react';
import { Link, useParams } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ArrowLeft, ArrowRight, Calendar, Tag } from 'lucide-react';
import { MarketingPageHead } from '../MarketingPageHead';
import { JsonLd, siteOrigin } from '../seo/JsonLd';
import { buildBlogPostingSchema, buildBreadcrumbSchema } from '../seo/schema';
import { getBlogPost, type BlogPost } from '../api';
import { renderMarkdown } from '../markdown';

const FADE_UP = {
  hidden: { opacity: 0, y: 20 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.6, ease: [0.16, 1, 0.3, 1] as any } },
};

const STAGGER = {
  hidden: { opacity: 0 },
  visible: { opacity: 1, transition: { staggerChildren: 0.08 } },
};

export function BlogPostPage() {
  const { slug } = useParams<{ slug: string }>();
  const [post, setPost] = React.useState<BlogPost | null | undefined>(undefined);

  React.useEffect(() => {
    let cancelled = false;
    if (!slug) { setPost(null); return; }
    getBlogPost(slug)
      .then((p) => { if (!cancelled) setPost(p); })
      .catch(() => { if (!cancelled) setPost(null); });
    return () => { cancelled = true; };
  }, [slug]);

  if (post === undefined) return <Skeleton />;
  if (post === null) return <NotFound slug={slug} />;

  const seoTitle = post.seo_title?.trim() || post.title;
  const seoDescription = post.seo_description?.trim() || post.excerpt?.trim() || undefined;
  const seoImage = post.og_image_url ?? post.cover_url ?? undefined;
  const html = renderMarkdown(post.body_md, { dropFirstH1: true });

  const origin = siteOrigin();
  const postingSchema = buildBlogPostingSchema({ post, origin });
  const breadcrumbSchema = buildBreadcrumbSchema(
    [
      { name: 'Home', path: '/' },
      { name: 'Blog', path: '/blog' },
      { name: post.title, path: `/blog/${post.slug}` },
    ],
    origin,
  );

  return (
    <div className="overflow-x-hidden">
      <MarketingPageHead
        title={seoTitle}
        description={seoDescription}
        image={seoImage}
        path={`/blog/${post.slug}`}
        ogType="article"
        articlePublishedTime={post.published_at ?? undefined}
        articleModifiedTime={post.updated_at ?? post.published_at ?? undefined}
        articleAuthor={post.author_name ?? undefined}
      />
      <JsonLd id="blog-posting" schema={postingSchema} />
      <JsonLd id="breadcrumb" schema={breadcrumbSchema} />

      <article className="py-12 lg:py-20">
        <div className="max-w-3xl mx-auto px-6 lg:px-8">
          <motion.div initial="hidden" animate="visible" variants={STAGGER}>
            <motion.div variants={FADE_UP}>
              <Link
                to="/blog"
                className="inline-flex items-center gap-1.5 text-[12.5px] font-semibold text-ink/55 dark:text-white/55 hover:text-coral dark:hover:text-coral-400 transition-colors"
              >
                <ArrowLeft className="h-3.5 w-3.5" />
                All posts
              </Link>
            </motion.div>

            <motion.div variants={FADE_UP} className="mt-6 flex flex-wrap items-center gap-2.5 text-[12px] text-ink/55 dark:text-white/55 tabular-nums">
              <Calendar className="h-3.5 w-3.5" />
              {post.published_at
                ? new Date(post.published_at).toLocaleDateString(undefined, { year: 'numeric', month: 'long', day: 'numeric' })
                : '—'}
              {post.author_name && <span className="text-ink/45 dark:text-white/40">·</span>}
              {post.author_name && <span className="font-semibold text-ink dark:text-white">{post.author_name}</span>}
            </motion.div>

            <motion.h1
              variants={FADE_UP}
              className="mt-4 font-light text-display-xl text-ink dark:text-white tracking-[-0.035em] leading-[1.05] text-balance"
            >
              {post.title}
            </motion.h1>

            {post.excerpt && (
              <motion.p
                variants={FADE_UP}
                className="mt-5 text-body-lg text-ink/70 dark:text-white/65 leading-relaxed text-pretty"
              >
                {post.excerpt}
              </motion.p>
            )}

            {post.tags.length > 0 && (
              <motion.ul variants={FADE_UP} className="mt-5 flex flex-wrap gap-1.5">
                {post.tags.map((t) => (
                  <li
                    key={t}
                    className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full bg-pearl-100 dark:bg-white/[0.06] text-[11px] font-semibold text-ink/65 dark:text-white/65"
                  >
                    <Tag className="h-2.5 w-2.5" />
                    {t}
                  </li>
                ))}
              </motion.ul>
            )}

            {post.cover_url && (
              <motion.img
                variants={FADE_UP}
                src={post.cover_url}
                alt=""
                className="mt-10 w-full rounded-3xl"
                loading="eager"
              />
            )}

            <motion.div
              variants={FADE_UP}
              className="mt-10 prose-content"
              // The markdown renderer escapes HTML; class hooks are inline in the output.
              dangerouslySetInnerHTML={{ __html: html }}
            />
          </motion.div>
        </div>
      </article>

      <FooterCTA />
    </div>
  );
}

function Skeleton() {
  return (
    <div className="py-12 lg:py-20">
      <div className="max-w-3xl mx-auto px-6 lg:px-8 space-y-5">
        <div className="h-3 w-24 rounded-full bg-ink/10 dark:bg-white/10 animate-pulse" />
        <div className="h-12 w-3/4 rounded-2xl bg-ink/10 dark:bg-white/10 animate-pulse" />
        <div className="h-3 w-1/2 rounded-full bg-ink/10 dark:bg-white/10 animate-pulse" />
        <div className="h-64 w-full rounded-3xl bg-ink/10 dark:bg-white/10 animate-pulse mt-8" />
        <div className="space-y-3 mt-6">
          {Array.from({ length: 6 }).map((_, i) => (
            <div key={i} className="h-3 w-full rounded-full bg-ink/10 dark:bg-white/10 animate-pulse" />
          ))}
        </div>
      </div>
    </div>
  );
}

function NotFound({ slug }: { slug: string | undefined }) {
  return (
    <div className="py-20">
      <MarketingPageHead title={slug ?? 'Post not found'} noindex />
      <div className="max-w-md mx-auto px-6 lg:px-8 text-center">
        <p className="text-[11px] font-bold uppercase tracking-[0.18em] text-coral">404</p>
        <h1 className="mt-3 font-light text-display-md text-ink dark:text-white tracking-[-0.025em]">
          Post not found.
        </h1>
        <p className="mt-3 text-[14.5px] text-ink/65 dark:text-white/65 leading-relaxed">
          The URL might have been retired, or the post is no longer published.
        </p>
        <div className="mt-6 flex flex-wrap gap-3 justify-center">
          <Link
            to="/blog"
            className="inline-flex items-center justify-center gap-2 h-11 px-5 rounded-full bg-coral text-white text-[13.5px] font-semibold shadow-cta hover:bg-coral-600 transition-colors focus-coral"
          >
            <ArrowLeft className="h-3.5 w-3.5" /> All posts
          </Link>
        </div>
      </div>
    </div>
  );
}

function FooterCTA() {
  return (
    <section className="py-16 lg:py-20">
      <div className="max-w-site mx-auto px-6 lg:px-8">
        <div className="relative rounded-[32px] overflow-hidden bg-coral-gradient text-white p-10 lg:p-16 shadow-card-lg">
          <div aria-hidden className="absolute inset-0 pds-dotted opacity-15 mix-blend-overlay" />
          <div className="relative grid grid-cols-1 lg:grid-cols-12 gap-8 items-center">
            <div className="lg:col-span-7">
              <p className="text-[11px] font-bold uppercase tracking-[0.18em] text-white/80">Start free</p>
              <h2 className="mt-3 font-light text-display-md leading-[1.05] tracking-[-0.025em] text-balance">
                Try the product the post is <em className="font-serif">about.</em>
              </h2>
              <p className="mt-4 text-body-lg text-white/85 max-w-xl">
                Seven days free, no card to start.
              </p>
            </div>
            <div className="lg:col-span-5 flex flex-wrap gap-3 lg:justify-end">
              <Link
                to="/signup"
                className="inline-flex items-center justify-center gap-2 h-12 px-7 rounded-full bg-ink-900 text-white text-[14.5px] font-semibold hover:bg-black transition-colors"
              >
                Start free
                <ArrowRight className="h-4 w-4" />
              </Link>
              <Link
                to="/blog"
                className="inline-flex items-center justify-center gap-2 h-12 px-7 rounded-full bg-white/15 backdrop-blur-md text-white text-[14.5px] font-semibold ring-1 ring-white/25 hover:bg-white/25 transition-colors"
              >
                All posts
              </Link>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
